import { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import { Box, Chip, Paper, Typography, alpha, useTheme } from '@mui/material'

import { borderRadiusDefault } from '../../../../themes/theme'
import { maxInstallShareOf } from '../../helpers/relativeMetric'
import { centredDialogActionsSx } from '../../helpers/styles'
import { useMotivationDiagnosisDetail } from '../../hooks/useMotivationDiagnosisDetail'
import { useUAInsightsMeta } from '../../hooks/useUAInsightsMeta'
import { useUAInsightsNavigation } from '../../hooks/useUAInsightsNavigation'
import { useUATaxonomy } from '../../hooks/useUATaxonomy'
import { CreativeAdCardGrid } from '../CreativeAdCardGrid/CreativeAdCardGrid'
import { DualTrackBar } from '../DualTrackBar/DualTrackBar'
import { GameCreativeTagsTable } from '../GameCreativeTagsTable/GameCreativeTagsTable'
import { HeroAndTableSkeleton } from '../HeroAndTableSkeleton/HeroAndTableSkeleton'
import { SectionCard } from '../SectionCard/SectionCard'
import { UAInsightsDialog } from '../UAInsightsDialog/UAInsightsDialog'
import { UAInsightsDialogActions } from '../UAInsightsDialogActions/UAInsightsDialogActions'

/**
 * Drilldown for an aligned card on the "What's working, what's not?" section
 * of the UA Insights game page. Confirms the source game's strength on a
 * single motivation, surfaces the tags driving it, then benchmarks against
 * the genre's top three creatives so the user can spot opportunities to
 * amplify the alignment further.
 */
export const AlignmentDetailsDialog: FC = () => {
  const { t } = useTranslation('ua-insights')
  const { params, close, canGoBack, goBack } = useUAInsightsNavigation()
  const { tagLabel, motivationLabel, genreLabel } = useUATaxonomy()
  const { gameId: gameIdParam } = useParams<{ gameId: string }>()
  const sourceGameId = gameIdParam ?? null

  const open = params.dialog === 'alignment'
  const motivationId = open ? params.motivationId : null
  const { data, isLoading, isError, refetch } = useMotivationDiagnosisDetail(motivationId, sourceGameId)
  const { motivationById } = useUAInsightsMeta()

  const theme = useTheme()
  const accent = theme.palette.success.main
  const motivation = data?.motivation ?? (motivationId ? motivationById(motivationId) : undefined)
  const resolvedMotivation = motivation ? motivationLabel(motivation) : ''

  const genreMaxInstallShare = useMemo(() => (data ? maxInstallShareOf(data.genreTop3) : 0), [data])

  return (
    <UAInsightsDialog
      open={open}
      onClose={close}
      title={data ? t('alignment_dialog_title', { motivation: resolvedMotivation, game: data.game.name }) : t('alignment_dialog_title_fallback')}
      canGoBack={canGoBack}
      onGoBack={goBack}
      showNetworkSelector
      hasData={!!data}
      isLoading={isLoading}
      isError={isError}
      onRetry={refetch}
      loadingPlaceholder={<HeroAndTableSkeleton sections={3} />}
      notFoundFallback={
        <Typography variant="body2" color="text.secondary">
          {t('alignment_not_found')}
        </Typography>
      }
      actions={<UAInsightsDialogActions disabled={!data} />}
      actionsSx={centredDialogActionsSx}
    >
      {data && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Paper
            variant="outlined"
            sx={{
              p: 2.5,
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              borderColor: alpha(accent, 0.3),
              backgroundColor: alpha(accent, 0.05),
              borderRadius: borderRadiusDefault,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: accent }}>
              <CheckCircleRoundedIcon fontSize="small" />
              <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: '0.08em', lineHeight: 1.2 }}>
                {t('alignment_overline')}
              </Typography>
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
              {t('alignment_subtitle', { motivation: resolvedMotivation, game: data.game.name })}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('alignment_body', { motivation: resolvedMotivation, game: data.game.name })}
            </Typography>
            <Box sx={{ mt: 0.5 }}>
              <DualTrackBar
                gameDesign={data.scores.gameDesign ?? 0}
                creativeAds={data.scores.creativeAds}
                gameDesignColor={theme.palette.secondary.main}
                creativeAdsColor={theme.palette.primary.main}
                gameDesignLabel={t('motivation_source_game_design')}
                creativeAdsLabel={t('motivation_source_creative_ads')}
              />
            </Box>
          </Paper>

          {data.gameTopTagsForMotivation.length > 0 && (
            <SectionCard title={t('alignment_whats_working_heading', { game: data.game.name })}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('alignment_whats_working_body', { motivation: resolvedMotivation, game: data.game.name })}
                </Typography>
                <GameCreativeTagsTable tags={data.gameTopTagsForMotivation} totalAds={data.gameCreativeIdsWithMotivation.length} />
              </Box>
            </SectionCard>
          )}

          {data.genreTopTagsRecipe.length > 0 && (
            <SectionCard title={t('alignment_genre_benchmark_heading', { genre: genreLabel(data.game.genre) })}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('alignment_genre_benchmark_subtitle', { motivation: resolvedMotivation, genre: genreLabel(data.game.genre) })}
                </Typography>
                <GameCreativeTagsTable tags={data.genreTopTagsRecipe} totalAds={data.genreTop3.length} />
              </Box>
            </SectionCard>
          )}

          {data.genreTop3.length > 0 && (
            <SectionCard title={t('alignment_inspiration_heading', { genre: genreLabel(data.game.genre) })} disableBodyPadding>
              <CreativeAdCardGrid rows={data.genreTop3} maxInstallShare={genreMaxInstallShare} />
            </SectionCard>
          )}

          <SectionCard title={t('alignment_amplify_heading')}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Typography variant="body2" color="text.secondary">
                {data.genreAmplifyTags.length > 0
                  ? t('alignment_amplify_body', { motivation: resolvedMotivation, game: data.game.name, genre: genreLabel(data.game.genre) })
                  : t('alignment_amplify_empty', { game: data.game.name })}
              </Typography>
              {data.genreAmplifyTags.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {data.genreAmplifyTags.map((tag) => (
                    <Chip
                      key={tag.tag.id}
                      label={`${tagLabel(tag.tag)} · ${tag.count}`}
                      variant="outlined"
                      sx={{ borderColor: alpha(accent, 0.4), color: accent, fontWeight: 'bold' }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          </SectionCard>
        </Box>
      )}
    </UAInsightsDialog>
  )
}
